//====== WR data 24/09/2026 — SmartFlow Importer: etapa de Pré-requisitos via BBP (Business
// Blueprint). Sobe o .docx do BBP do projeto, a IA sugere Depósitos/Grupos de Item/Grupos de PN
// (texto livre, sem formato padronizado — ver proposta em
// ~/Developer/Documentacao/SmartFlow Importer - Pré-requisitos via BBP - Proposta.md), o usuário
// revisa/edita as 3 listas e confirma. Só depois de confirmado (`validadoEm` preenchido) as abas
// de Item/Parceiro do Importer liberam (ver preRequisitosEstaoCompletos, usado pelo componente
// pai — a integração no seletor de modo do SmartFlowImporter.js é o próximo passo, ainda não
// feita aqui).
//======
import React, { useEffect, useState } from "react"
import { Card, CardBody, CardHeader, Table, Input, Button, Badge, Alert, Row, Col } from "reactstrap"

import {
  processarBbp,
  lerPreRequisitos,
  salvarPreRequisitos,
  baixarBbpOriginal,
  excluirBbp,
} from "services/smartflow/bbpApi"
import { buscarDepositosSap, buscarGruposItemSap } from "services/smartflow/itemSapWrite"
import { buscarGruposParceiroSap } from "services/smartflow/smartflowSapWrite"
import { gravarDepositoSap, gravarGrupoItemSap, gravarGrupoPnSap } from "services/smartflow/preRequisitosSapWrite"
import {
  sugestaoParaLinhas,
  novaLinhaManual,
  linhasParaPayload,
  preRequisitosEstaoCompletos,
  precisaEscolherPapel,
  precisaGravarNoSap,
  mesclarComSap,
  formatConfianca,
  formatarData,
} from "components/SmartFlow/preRequisitosLogic"

//====== Tag de origem por linha — pedido do usuário: diferenciar visualmente o que já existe
// cadastrado no SAP (consultado ao vivo) do que só o BBP sugere (a IA nunca é a palavra final).
// Item digitado manualmente na tela não ganha tag (não precisa — é óbvio pra quem digitou).
function OrigemBadge({ linha }) {
  if (linha.origem === "sap") return <Badge color="info" className="ml-2">Já cadastrado no SAP</Badge>
  if (linha.origem === "bbp") return <ConfiancaBadge confianca={linha.confiancaIA} />
  return null
}

function ConfiancaBadge({ confianca }) {
  if (!confianca) return null
  const cor = { alta: "success", media: "warning", baixa: "secondary" }[confianca] || "secondary"
  return (
    <Badge color={cor} className="ml-2">
      BBP — {formatConfianca(confianca)}
    </Badge>
  )
}

//====== Uma das 3 grades editáveis (Depósitos / Grupos de Item / Grupos de PN) — mesmo
// comportamento pras três, só muda o título, se mostra a coluna de código e (só Grupos de PN) a
// coluna de Papel.
// WR data 30/09/2026 — Motivo da alteração: pedido do usuário — poder escolher incluir ou não cada
// sugestão do BBP na gravação real no SAP, em vez de tudo virar gravação automática. `incluir`
// desmarcado mantém a linha visível (não é a mesma coisa que "Remover", que apaga de vez), só fica
// de fora quando o usuário confirmar. Uma linha já cadastrada no SAP (origem "sap") não tem o que
// incluir/excluir — já existe, o checkbox fica travado marcado.
function ListaEditavel({ titulo, linhas, setLinhas, mostrarPapel, codigoGeradoPeloSap }) {
  const atualizarLinha = (id, campo, valor) => {
    setLinhas(linhas.map((l) => (l.id === id ? { ...l, [campo]: valor, origem: "manual", erroGravacao: null } : l)))
  }
  const alternarIncluir = (id) => {
    setLinhas(linhas.map((l) => (l.id === id ? { ...l, incluir: !l.incluir, erroGravacao: null } : l)))
  }
  const removerLinha = (id) => setLinhas(linhas.filter((l) => l.id !== id))
  const adicionarLinha = () => setLinhas([...linhas, novaLinhaManual()])

  return (
    <Card className="mb-3">
      <CardHeader className="d-flex justify-content-between align-items-center">
        <span>
          <strong>{titulo}</strong> <Badge color="light">{linhas.length}</Badge>
        </span>
        <Button size="sm" color="secondary" outline onClick={adicionarLinha}>
          + Adicionar
        </Button>
      </CardHeader>
      <CardBody>
        {linhas.length === 0 ? (
          <p className="text-muted mb-0">
            Nada encontrado no BBP para esta seção — adicione manualmente se o cliente definiu algo, ou
            deixe em branco se não se aplica.
          </p>
        ) : (
          <Table size="sm" responsive>
            <thead>
              <tr>
                <th style={{ width: 70 }}>Incluir</th>
                <th>Nome</th>
                <th style={{ width: 140 }}>Código</th>
                {mostrarPapel && <th style={{ width: 150 }}>Papel</th>}
                <th style={{ width: 60 }} />
              </tr>
            </thead>
            <tbody>
              {linhas.map((linha) => {
                const jaExisteNoSap = linha.origem === "sap"
                return (
                  <tr key={linha.id}>
                    <td className="text-center">
                      <Input
                        type="checkbox"
                        checked={jaExisteNoSap ? true : linha.incluir !== false}
                        disabled={jaExisteNoSap}
                        onChange={() => alternarIncluir(linha.id)}
                        title={jaExisteNoSap ? "Já existe no SAP" : "Incluir na gravação no SAP"}
                      />
                    </td>
                    <td>
                      <Input
                        bsSize="sm"
                        value={linha.nome}
                        disabled={jaExisteNoSap}
                        onChange={(e) => atualizarLinha(linha.id, "nome", e.target.value)}
                      />
                      <OrigemBadge linha={linha} />
                      {linha.erroGravacao && (
                        <div className="text-danger small mt-1">Falha ao gravar no SAP: {linha.erroGravacao}</div>
                      )}
                    </td>
                    <td>
                      <Input
                        bsSize="sm"
                        value={linha.codigo || ""}
                        disabled={jaExisteNoSap || codigoGeradoPeloSap}
                        placeholder={codigoGeradoPeloSap && !jaExisteNoSap ? "gerado pelo SAP" : ""}
                        onChange={(e) => atualizarLinha(linha.id, "codigo", e.target.value)}
                      />
                    </td>
                    {mostrarPapel && (
                      <td>
                        <Input
                          type="select"
                          bsSize="sm"
                          value={linha.papel || ""}
                          disabled={jaExisteNoSap || linha.incluir === false}
                          onChange={(e) => atualizarLinha(linha.id, "papel", e.target.value || null)}
                        >
                          <option value="">— escolha —</option>
                          <option value="cliente">Cliente</option>
                          <option value="fornecedor">Fornecedor</option>
                        </Input>
                      </td>
                    )}
                    <td>
                      {jaExisteNoSap ? (
                        <span className="text-muted small" title="Já existe no SAP — remover aqui não apaga o cadastro real, e a linha volta a aparecer na próxima atualização da tela.">
                          Já existe no SAP
                        </span>
                      ) : (
                        <Button size="sm" color="link" className="text-danger" onClick={() => removerLinha(linha.id)}>
                          Remover
                        </Button>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </Table>
        )}
      </CardBody>
    </Card>
  )
}

export default function SmartFlowPreRequisitosForm({ projectId, userEmail, serviceLayerUrl, onValidado }) {
  const [carregando, setCarregando] = useState(true)
  const [processando, setProcessando] = useState(false)
  const [salvando, setSalvando] = useState(false)
  const [excluindo, setExcluindo] = useState(false)
  const [erro, setErro] = useState(null)
  const [bbpFileName, setBbpFileName] = useState(null)
  const [bbpHtml, setBbpHtml] = useState(null)
  const [bbpGcsOriginalPath, setBbpGcsOriginalPath] = useState(null)
  const [bbpEnviadoPor, setBbpEnviadoPor] = useState(null)
  const [bbpProcessadoEm, setBbpProcessadoEm] = useState(null)
  const [baixando, setBaixando] = useState(false)
  const [validadoEm, setValidadoEm] = useState(null)
  const [depositos, setDepositos] = useState([])
  const [gruposItem, setGruposItem] = useState([])
  const [gruposPn, setGruposPn] = useState([])
  // Cache das 3 listas do SAP (consultadas 1x ao carregar a tela) — reaproveitada tanto pra
  // mesclar com o que já foi salvo quanto com uma sugestão nova de um BBP recém-processado, sem
  // precisar consultar a Service Layer de novo a cada upload.
  const [sapListas, setSapListas] = useState({ depositos: [], gruposItem: [], gruposPn: [] })

  useEffect(() => {
    let cancelado = false
    async function carregar() {
      // Consulta ao vivo o que JÁ existe no SAP (Depósitos/Grupos) — pedido do usuário: a grade
      // deve mostrar tanto o que o BBP sugere quanto o que já está cadastrado de verdade no
      // tenant, cada um com sua própria tag. Falha aqui nunca bloqueia a tela (o projeto pode
      // ainda não ter Service Layer configurada, ou a consulta pode falhar) — só fica sem a
      // mesclagem automática dessa vez.
      let sap = { depositos: [], gruposItem: [], gruposPn: [] }
      if (serviceLayerUrl) {
        try {
          const [deps, itens, pns] = await Promise.all([
            buscarDepositosSap(serviceLayerUrl),
            buscarGruposItemSap(serviceLayerUrl),
            buscarGruposParceiroSap(null, serviceLayerUrl),
          ])
          sap = { depositos: deps, gruposItem: itens, gruposPn: pns }
        } catch (error) {
          console.warn("[SmartFlow] Falha ao consultar Depósitos/Grupos já cadastrados no SAP:", error.message)
        }
      }
      if (cancelado) return
      setSapListas(sap)

      try {
        const atual = await lerPreRequisitos(projectId)
        if (cancelado) return
        setBbpFileName(atual?.bbpOriginalFileName || null)
        setBbpHtml(atual?.bbpHtml || null)
        setBbpGcsOriginalPath(atual?.bbpGcsOriginalPath || null)
        setBbpEnviadoPor(atual?.bbpEnviadoPor || null)
        setBbpProcessadoEm(atual?.bbpProcessadoEm || null)
        setValidadoEm(atual?.validadoEm || null)
        setDepositos(mesclarComSap(sugestaoParaLinhas(atual?.depositos), sap.depositos))
        setGruposItem(mesclarComSap(sugestaoParaLinhas(atual?.gruposItem), sap.gruposItem))
        setGruposPn(mesclarComSap(sugestaoParaLinhas(atual?.gruposPn), sap.gruposPn))
      } catch (error) {
        // projeto sem pré-requisitos salvos ainda não é erro — só começa vazio (+ o que já existe
        // no SAP, se a consulta acima deu certo).
        setDepositos(mesclarComSap([], sap.depositos))
        setGruposItem(mesclarComSap([], sap.gruposItem))
        setGruposPn(mesclarComSap([], sap.gruposPn))
      } finally {
        if (!cancelado) setCarregando(false)
      }
    }
    carregar()
    return () => {
      cancelado = true
    }
  }, [projectId, serviceLayerUrl])

  const handleUpload = async (e) => {
    const arquivo = e.target.files?.[0]
    e.target.value = "" // permite subir o mesmo arquivo de novo depois de corrigir algo
    if (!arquivo) return

    setErro(null)
    setProcessando(true)
    try {
      const resultado = await processarBbp(projectId, arquivo, userEmail)
      setBbpFileName(resultado.bbpOriginalFileName)
      setBbpHtml(resultado.bbpHtml)
      setBbpGcsOriginalPath(resultado.bbpGcsOriginalPath || null)
      setBbpEnviadoPor(resultado.bbpEnviadoPor || null)
      setBbpProcessadoEm(resultado.bbpProcessadoEm || null)
      setDepositos(mesclarComSap(sugestaoParaLinhas(resultado.sugestao?.depositos), sapListas.depositos))
      setGruposItem(mesclarComSap(sugestaoParaLinhas(resultado.sugestao?.gruposItem), sapListas.gruposItem))
      setGruposPn(mesclarComSap(sugestaoParaLinhas(resultado.sugestao?.gruposPn), sapListas.gruposPn))
      setValidadoEm(null) // um BBP novo invalida a confirmação anterior — precisa revisar de novo
    } catch (error) {
      setErro(error?.response?.data?.error || "Falha ao processar o BBP. Confirme que é um .docx válido.")
    } finally {
      setProcessando(false)
    }
  }

  //====== WR data 30/09/2026 — Motivo da alteração: pedido do usuário — 2 etapas em vez de 1.
  // Antes "Salvar" só validava a lista (nunca criava nada de verdade no SAP). Agora: (1) grava no
  // SAP, uma linha de cada vez, tudo que estiver marcado "incluir" e ainda não existir (origem !==
  // "sap"); (2) só libera Item/Parceiro depois que TODAS as gravações derem certo — uma falha
  // qualquer para o processo ali (as linhas já gravadas com sucesso ANTES da falha ficam
  // atualizadas com o código real, não perde o que já deu certo se o usuário tentar de novo).
  // Sequencial (não Promise.all) de propósito: são requisições reais de escrita na Service Layer do
  // cliente, e chamadas concorrentes demais num tenant pequeno de homologação é pedir problema.
  async function gravarPendentesNoSap(linhas, setLinhas, gravarFn) {
    const atual = [...linhas]
    for (let i = 0; i < atual.length; i++) {
      const linha = atual[i]
      if (!precisaGravarNoSap(linha)) continue
      try {
        const resultado = await gravarFn(linha)
        atual[i] = { ...linha, codigo: resultado.codigo, nome: resultado.nome, origem: "sap", erroGravacao: null }
        setLinhas([...atual])
      } catch (error) {
        atual[i] = { ...linha, erroGravacao: error.message }
        setLinhas([...atual])
        throw new Error(`"${linha.nome}": ${error.message}`)
      }
    }
    return atual
  }

  const handleConfirmar = async () => {
    setErro(null)

    const pendentesPapel = gruposPn.filter(precisaEscolherPapel)
    if (pendentesPapel.length > 0) {
      setErro(
        `Escolha o papel (Cliente ou Fornecedor) de: ${pendentesPapel.map((l) => l.nome).join(", ")} — ` +
          `o SAP não tem um grupo que sirva para os dois ao mesmo tempo.`
      )
      return
    }

    setSalvando(true)
    try {
      const depositosGravados = await gravarPendentesNoSap(depositos, setDepositos, (d) =>
        gravarDepositoSap(serviceLayerUrl, d)
      )
      const gruposItemGravados = await gravarPendentesNoSap(gruposItem, setGruposItem, (g) =>
        gravarGrupoItemSap(serviceLayerUrl, g)
      )
      const gruposPnGravados = await gravarPendentesNoSap(gruposPn, setGruposPn, (g) =>
        gravarGrupoPnSap(serviceLayerUrl, g)
      )

      const salvo = await salvarPreRequisitos(projectId, {
        depositos: linhasParaPayload(depositosGravados),
        gruposItem: linhasParaPayload(gruposItemGravados),
        gruposPn: linhasParaPayload(gruposPnGravados),
        validadoPor: userEmail || null,
      })
      setValidadoEm(salvo.validadoEm)
      if (onValidado) onValidado(salvo)
    } catch (error) {
      setErro(
        error?.response?.data?.error ||
          error?.message ||
          "Falha ao gravar os pré-requisitos no SAP."
      )
    } finally {
      setSalvando(false)
    }
  }

  // Abre o HTML do BBP numa aba própria — renderizado inline (dentro do card, com imagens no
  // tamanho original do documento) ficava ruim, principalmente logos/capas em largura total.
  // Numa aba nova, o navegador cuida do próprio scroll/zoom; só precisa de um reset básico de CSS
  // pra imagem nunca estourar a largura da janela.
  const handleConsultarBbp = () => {
    const documentoCompleto = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${
      bbpFileName || "BBP"
    }</title><style>body{font-family:-apple-system,Helvetica,Arial,sans-serif;max-width:900px;margin:24px auto;padding:0 16px;line-height:1.5;color:#212529}img{max-width:100%;height:auto}table{border-collapse:collapse;width:100%}td,th{border:1px solid #dee2e6;padding:6px 10px}</style></head><body>${bbpHtml}</body></html>`
    const blob = new Blob([documentoCompleto], { type: "text/html" })
    const url = URL.createObjectURL(blob)
    window.open(url, "_blank")
    // não revoga a URL na hora — a aba nova ainda está carregando o conteúdo dela; o navegador
    // libera a memória sozinho quando aquela aba/contexto fecha.
  }

  const handleBaixarOriginal = async () => {
    setBaixando(true)
    setErro(null)
    try {
      await baixarBbpOriginal(projectId, bbpFileName)
    } catch (error) {
      setErro("Falha ao baixar o BBP original.")
    } finally {
      setBaixando(false)
    }
  }

  const handleExcluirBbp = async () => {
    // eslint-disable-next-line no-alert
    const ok = window.confirm(
      `Excluir o BBP "${bbpFileName}"? Remove o arquivo e reseta as 3 listas — a validação precisará ` +
        `ser refeita do zero (com um novo BBP, ou preenchendo manualmente). Esta ação não pode ser desfeita.`
    )
    if (!ok) return

    setExcluindo(true)
    setErro(null)
    try {
      await excluirBbp(projectId)
      setBbpFileName(null)
      setBbpHtml(null)
      setBbpGcsOriginalPath(null)
      setBbpEnviadoPor(null)
      setBbpProcessadoEm(null)
      setValidadoEm(null)
      // o que já existia no SAP continua valendo (não depende do BBP excluído) — só o que tinha
      // vindo do BBP/manual some junto com ele.
      setDepositos(mesclarComSap([], sapListas.depositos))
      setGruposItem(mesclarComSap([], sapListas.gruposItem))
      setGruposPn(mesclarComSap([], sapListas.gruposPn))
      if (onValidado) onValidado({})
    } catch (error) {
      setErro(error?.response?.data?.error || "Falha ao excluir o BBP.")
    } finally {
      setExcluindo(false)
    }
  }

  if (carregando) {
    return (
      <Card>
        <CardBody>
          <p className="text-muted mb-0">Carregando pré-requisitos...</p>
        </CardBody>
      </Card>
    )
  }

  const completo = preRequisitosEstaoCompletos({ validadoEm })

  return (
    <Card>
      <CardBody>
        <Row className="align-items-center mb-3">
          <Col>
            <h4 className="mb-0">Pré-requisitos (BBP)</h4>
            <p className="text-muted mb-0">
              Suba o BBP do projeto para a IA sugerir Depósitos, Grupos de Item e Grupos de Parceiro de
              Negócio — revise e confirme antes de liberar as etapas de importação.
            </p>
          </Col>
          <Col xs="auto">
            <Input type="file" accept=".docx" onChange={handleUpload} disabled={processando} />
          </Col>
        </Row>

        {erro && <Alert color="danger">{erro}</Alert>}
        {processando && <Alert color="info">Processando o BBP (extração + sugestão da IA)...</Alert>}

        {bbpFileName && (
          <Alert color="light" className="d-flex justify-content-between align-items-center">
            <span>
              BBP carregado: <strong>{bbpFileName}</strong>
              <br />
              <small className="text-muted">
                Enviado por {bbpEnviadoPor || "—"} em {formatarData(bbpProcessadoEm)}
              </small>
            </span>
            <span>
              {bbpHtml && (
                <Button size="sm" color="link" onClick={handleConsultarBbp}>
                  Consultar BBP (nova aba)
                </Button>
              )}
              {bbpGcsOriginalPath && (
                <Button size="sm" color="link" onClick={handleBaixarOriginal} disabled={baixando}>
                  {baixando ? "Baixando..." : "Baixar original (.docx)"}
                </Button>
              )}
              <Button size="sm" color="link" className="text-danger" onClick={handleExcluirBbp} disabled={excluindo}>
                {excluindo ? "Excluindo..." : "Excluir BBP"}
              </Button>
            </span>
          </Alert>
        )}

        <ListaEditavel titulo="Depósitos" linhas={depositos} setLinhas={setDepositos} />
        <ListaEditavel
          titulo="Grupos de Item"
          linhas={gruposItem}
          setLinhas={setGruposItem}
          codigoGeradoPeloSap
        />
        <ListaEditavel
          titulo="Grupos de Cliente/Fornecedor"
          linhas={gruposPn}
          setLinhas={setGruposPn}
          mostrarPapel
          codigoGeradoPeloSap
        />

        <div className="d-flex justify-content-between align-items-center">
          {completo ? (
            <Badge color="success">Pré-requisitos confirmados — etapas seguintes liberadas</Badge>
          ) : (
            <Badge color="warning">Confirme os pré-requisitos para liberar Item/Parceiro</Badge>
          )}
          <Button color="primary" onClick={handleConfirmar} disabled={salvando}>
            {salvando ? "Gravando no SAP..." : "Gravar no SAP e confirmar"}
          </Button>
        </div>
      </CardBody>
    </Card>
  )
}
